<template>
  <main class="container is-fluid columns is-centered">
    <div class="column is-half-desktop is-four-fifths-tablet">
      <h1 class="title">Reset your password</h1>
      <div
        class="notification is-success"
        v-if="success"
      >
        Your password has been successfully reset.
        <router-link :to="{ name: 'login' }">Login</router-link>
      </div>
      <form
        v-on:submit.prevent="submit"
        v-else
      >
        <div
          class="notification is-danger"
          v-if="mainError?.length"
        >
          <button
            type="button"
            class="delete"
            v-on:click="mainError = null"
          ></button>
          <p
            v-for="message in mainError"
            :key="message"
          >
            {{ message }}
          </p>
        </div>
        <div
          class="notification is-warning"
          v-if="fieldErrors.token?.length"
        >
          <button
            type="button"
            class="delete"
            v-on:click="delete fieldErrors.token"
          ></button>
          <p
            v-for="message in fieldErrors.token"
            :key="message"
          >
            {{ message }}
          </p>
        </div>
        <div class="field">
          <label
            class="label"
            for="password"
            >New password</label
          >
          <div class="control">
            <input
              type="password"
              required
              class="input"
              :class="{ 'is-danger': fieldErrors.password }"
              :disabled="loading || undefined"
              v-model="password"
            />
            <template v-if="fieldErrors.password">
              <p
                class="help is-danger"
                v-for="err in fieldErrors.password"
                :key="err"
              >
                {{ err }}
              </p>
            </template>
          </div>
        </div>
        <div class="field">
          <label
            class="label"
            for="confirmPassword"
            >Confirm password</label
          >
          <div class="control">
            <input
              type="password"
              required
              class="input"
              :class="{ 'is-danger': fieldErrors.confirmPassword }"
              :disabled="loading || undefined"
              v-model="confirmPassword"
            />
            <template v-if="fieldErrors.confirmPassword">
              <p
                class="help is-danger"
                v-for="err in fieldErrors.confirmPassword"
                :key="err"
              >
                {{ err }}
              </p>
            </template>
          </div>
        </div>
        <div class="field is-grouped is-grouped-centered">
          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :disabled="!password || !confirmPassword || undefined"
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  </main>
</template>

<script lang="ts">
import { mapActions } from 'pinia'
import { defineComponent } from 'vue'

import { errorParser, extractFieldErrors, isArkindexError } from '@/helpers'
import { useAuthStore } from '@/stores'

type FieldErrors = { [k in 'token' | 'password' | 'confirmPassword']?: string[] | undefined }

export default defineComponent({
  props: {
    uidb64: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    password: '',
    confirmPassword: '',
    loading: false,
    success: false,
    fieldErrors: {} as FieldErrors,
    mainError: null as string[] | null,
  }),
  methods: {
    ...mapActions(useAuthStore, ['resetPassword']),
    /**
     * Set field errors from an API error response, or clear them if the error is `null`.
     */
    setErrors(error: unknown) {
      this.fieldErrors = {}
      this.mainError = []
      if (!error) return
      if (!isArkindexError(error)) {
        this.mainError = [errorParser(error)]
        return
      }
      ;[this.fieldErrors, this.mainError] = extractFieldErrors(error.response.data, [
        'token',
        'password',
        'confirmPassword',
      ])
    },
    async submit() {
      if (!this.checkPasswords()) return

      this.success = false
      this.loading = true
      this.setErrors(null)
      try {
        await this.resetPassword({
          uidb64: this.uidb64,
          token: this.token,
          password: this.confirmPassword,
        })
        this.success = true
      } catch (err) {
        this.setErrors(err)
      } finally {
        this.loading = false
        this.password = ''
        this.confirmPassword = ''
      }
    },
    checkPasswords(): boolean {
      if (this.passwordMatch) return true
      this.fieldErrors = { confirmPassword: ['Passwords do not match'] }
      return false
    },
  },
  computed: {
    passwordMatch(): boolean {
      return !!this.password && !!this.confirmPassword && this.password === this.confirmPassword
    },
  },
})
</script>
